// Centralised help-text dictionary for every input field in the app.
// Each entry powers a hover/tap tooltip via <HoverTip id="..." />.
// Keep entries crisp — 1 sentence per field for description, guidance, and example.

export const FIELD_HELP = {
  // -------- CalcForm (Calculator) --------
  annual_contribution: {
    title: "Annual contribution",
    description: "Cash the business sets aside each year — retained earnings, excess corporate cash, or planned IBC premium dollars.",
    guidance: "Enter the realistic annual amount (in CAD) the corporation could direct into a policy. Not a one-time deposit.",
    example: "$200,000 — e.g., a 15-truck roofer with healthy retained earnings.",
  },
  horizon_years: {
    title: "Projection horizon",
    description: "How many years out we project bank-path vs IBC-path wealth.",
    guidance: "Most owners want a 10-year view; 5-yr and 10-yr summaries are always shown regardless.",
    example: "10 (default). Use 20 for a near-retirement planning window.",
  },
  num_vehicles: {
    title: "Financed vehicles / equipment",
    description: "Count of trucks, trailers, excavators, lifts, or any equipment with an active loan or lease payment.",
    guidance: "Count items that have a monthly payment to a bank, lender, or finance arm (Ford Credit, CWB, John Deere Financial, etc.).",
    example: "15 — typical mid-size Ontario roofing or HVAC fleet.",
  },
  avg_monthly_payment: {
    title: "Average monthly payment",
    description: "Mean monthly payment across the financed fleet — combines principal + interest.",
    guidance: "Take total monthly fleet payments ÷ number of vehicles. Round to nearest $50.",
    example: "$950/mo — typical mid-spec service truck on 5-yr financing.",
  },
  avg_loan_interest_rate_pct: {
    title: "Average loan interest rate",
    description: "Annual rate (APR) on the fleet loans — what the lender charges you.",
    guidance: "Enter as a percentage (e.g., 7.9 for 7.9%). If unsure, 7%–9% is typical for commercial vehicle loans in 2026.",
    example: "7.9% — current average for Canadian commercial fleet financing.",
  },
  avg_loan_term_years: {
    title: "Average loan term",
    description: "How long each loan runs before it's paid off.",
    guidance: "Most commercial fleet loans run 4–7 years. Use the term that matches when you'd refinance / replace.",
    example: "5 years — most common commercial truck financing term.",
  },
  num_key_employees: {
    title: "Key employees",
    description: "Staff you genuinely can't afford to lose — foremen, estimators, project managers, lead techs.",
    guidance: "Don't include front-line labour with high turnover. Count only people whose departure would cost meaningful revenue or rework.",
    example: "4 — foremen + senior estimator + project manager.",
  },
  annual_retention_bonus: {
    title: "Annual retention allocation per employee",
    description: "Dollars per key employee per year that you plan to commit to a Golden Handcuffs retention plan.",
    guidance: "This sits inside the policy cash value, vests over time, and gives the employee a reason to stay.",
    example: "$15,000/year/employee — common range is $10K–$25K.",
  },
  turnover_cost_per_employee: {
    title: "Turnover cost per employee",
    description: "All-in cost to replace one key employee — recruiting, training, lost productivity, dropped margin, customer impact.",
    guidance: "Industry rule-of-thumb: 1–1.5× annual salary for skilled roles. Higher for revenue-generating positions.",
    example: "$60,000 — typical replacement cost for a tenured foreman.",
  },
  retention_probability_boost: {
    title: "Retention probability boost",
    description: "How much the Golden Handcuffs plan reduces the chance a key employee leaves over the planning horizon.",
    guidance: "0% = no impact, 100% = guarantees retention. Realistic range is 30–60%.",
    example: "50% — middle of the realistic range; means you avoid roughly half the turnover events.",
  },
  dividend_rate: {
    title: "Dividend rate",
    description: "The participating dividend scale the issuing insurer credits to the policy each year.",
    guidance: "Use the 2026 declared scale — typically 6.25%–6.40% for major Canadian par insurers. Dividends are not guaranteed.",
    example: "6.35% — mid-point of the 2026 scale range.",
  },
  passive_tax_rate: {
    title: "Passive income tax rate",
    description: "Combined Ontario CCPC tax rate on passive corporate income (interest, dividends, etc.).",
    guidance: "Default is the 2026 combined rate of 50.17%. Override if the corporation has different provincial mix or status.",
    example: "50.17% — standard Ontario CCPC passive-income rate.",
  },
  passive_return_rate: {
    title: "Baseline corporate return",
    description: "Pre-tax annual return on the 'borrow from the bank' path — what the corporation would earn on GICs / HISA / conservative investments.",
    guidance: "Use 3–5% for conservative; higher only if the corp invests in marketable securities. Pre-tax — the tax drag is applied separately.",
    example: "5% — typical corporate HISA / 1-yr GIC ladder return.",
  },

  // -------- QuickBreakdown (public lead form) --------
  "qb-num-vehicles": {
    title: "Vehicles / equipment",
    description: "Count of trucks, trailers, or financed equipment in the business.",
    guidance: "Include anything with a monthly payment — service trucks, trailers, excavators, etc.",
    example: "10 — a typical mid-size contractor.",
  },
  "qb-annual-contribution": {
    title: "Annual retained earnings",
    description: "Rough cash left in the company each year after taxes and owner draws.",
    guidance: "An estimate is fine — the closer you can get, the more accurate the breakdown.",
    example: "$150,000 — a roofing or HVAC company doing $1–2M in revenue.",
  },
  "qb-num-key-employees": {
    title: "Key employees",
    description: "Foremen, estimators, project managers — staff you can't afford to lose.",
    guidance: "Count only people whose departure would meaningfully hurt the business.",
    example: "3 — two foremen + one senior estimator.",
  },
  "qb-full-name": {
    title: "Your full name",
    description: "Required so the advisor knows who to reach out to.",
    guidance: "Use the name you'd give an advisor in a first call.",
    example: "Mark Armstrong",
  },
  "qb-email": {
    title: "Email address",
    description: "Where we send your full breakdown and where your advisor will reach you first.",
    guidance: "Use your real working business email — that's where we send the report.",
    example: "mark@bayroofing.ca",
  },
  "qb-phone": {
    title: "Phone (optional)",
    description: "Optional but doubles your odds of getting a same-day callback.",
    guidance: "Best number to reach you during business hours.",
    example: "(705) 555-0188",
  },
  "qb-business": {
    title: "Business name (optional)",
    description: "Helps the advisor research your industry before the call.",
    guidance: "Leave blank if you'd rather discuss this on the call.",
    example: "Bay Roofing Ltd.",
  },
  "qb-notes": {
    title: "Anything we should know?",
    description: "Optional context — best time to reach you, specific questions, or concerns.",
    guidance: "Even one line helps the advisor prep the right conversation.",
    example: "Best to call after 4pm. Want to discuss key-person retention.",
  },

  // -------- Settings (advisor profile) --------
  logo: {
    title: "Firm logo",
    description: "Square PNG or JPG (under 600 KB). Appears on the upper-right of every PDF cover and in advisor branding spots.",
    guidance: "Use a square image for best results — circular logos work too. White or transparent background looks cleanest on the forest cover.",
    example: "yourfirm-logo-512x512.png",
  },
  advisor_name: {
    title: "Your advisor name",
    description: "Shown on every PDF cover page and in lead-notification emails.",
    guidance: "Use the name your clients will recognize — designations welcome.",
    example: "Joshua J Pink, IBC Practitioner",
  },
  firm_name: {
    title: "Firm name",
    description: "Your advisory firm or sole-proprietor business name.",
    guidance: "Appears under your name on the PDF cover.",
    example: "High Vibe Pinnacles Wealth",
  },
  email: {
    title: "Your contact email",
    description: "The email clients see on the PDF cover — what they should reply to.",
    guidance: "Use your business email, not a personal one.",
    example: "joshua@yourfirm.ca",
  },
  phone: {
    title: "Phone",
    description: "Your business phone — clients click it from the PDF or email to call you.",
    guidance: "Include area code in standard Canadian format.",
    example: "(705) 555-0188",
  },
  website: {
    title: "Website",
    description: "Your firm website. Optional — appears on the PDF cover if set.",
    guidance: "Just the domain — no https:// prefix needed.",
    example: "app.build-a-bankcalculator.solutions",
  },
  license_number: {
    title: "Licence number (optional)",
    description: "Your insurance / financial licence — gives the PDF additional credibility for regulated clients.",
    guidance: "Provincial LLQP or CIRO registration number is typical.",
    example: "LLQP-12345",
  },
  tagline: {
    title: "Tagline",
    description: "A one-line positioning statement that appears on the PDF cover, just under your firm name.",
    guidance: "Aim for 6–12 words. Describe who you serve and how.",
    example: "Build a bank, North Bay — IBC strategies for Ontario business owners",
  },
  notification_email: {
    title: "Lead-notification email",
    description: "Where the 'new lead' alert is sent the moment a prospect submits the public form.",
    guidance: "Use whichever inbox you actually check on your phone — speed matters for converting hot leads.",
    example: "jjp.highvibepinnacles89@gmail.com",
  },
  notifications_enabled: {
    title: "Email notifications",
    description: "Turn lead-notification emails on or off. Leads still appear in /leads either way.",
    guidance: "Leave enabled. Disable temporarily during vacations or holidays.",
    example: "ON (default).",
  },

  // -------- Save Scenario dialog (Calculator) --------
  client: {
    title: "Client name",
    description: "Required — used as the heading on the saved scenario and the PDF cover.",
    guidance: "Use the business owner's name, exactly as you'd address them.",
    example: "Mark Armstrong",
  },
  business: {
    title: "Business name",
    description: "Optional — appears below the client name on the PDF cover.",
    guidance: "Use the legal corporate name if you have it, or the operating name otherwise.",
    example: "Bay Roofing Ltd.",
  },
  notes: {
    title: "Scenario notes",
    description: "Optional — your internal note attached to the saved scenario.",
    guidance: "Capture context: which numbers were rough, what to revisit, when the follow-up is.",
    example: "Discussed 15-truck fleet, 4 foremen, Q2 follow-up scheduled.",
  },
};
